/* eslint-disable max-lines-per-function */
import constate from "constate";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { formToApiPayload } from "@design-lab/entities/composition/lib/design-project";
import { useDebounce } from "@design-lab/shared/hooks/useDebounce";
import type { DesignLabFormType } from "@design-lab/shared/types";
import { useUpdateWithDeliveryEstimates } from "./useUpdateWithDeliveryEstimates";
import { useDesignLabInit } from "./hooks/useDesignLabInit";

const DEBOUNCE_DELAY = 2000;

function useDesignProjectSyncCreator() {
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const updateProject = useUpdateWithDeliveryEstimates();

  // Use the new initialization system - it handles EVERYTHING
  const {
    stage,
    isInitialized,
    isLoading: isInitializing,
    error: initError,
    projectUuid,
    projectData,
    savedProject,
    productsMap,
    activeWorkflow,
    isProcessingWorkflow,
    isDesignLibraryOpen,
    isCanvaOpen,
    isAiDesignOpen,
    retryInit,
    reset,
    lastSavedDataRef
  } = useDesignLabInit();

  // Watch both forms and combine their values
  const artworkFormValues = useWatch({ control: artworkForm.control });
  const compositionFormValues = useWatch({ control: compositionForm.control });
  const formValues = { artwork: artworkFormValues, compositions: compositionFormValues };
  const debouncedFormValues = useDebounce(formValues, DEBOUNCE_DELAY);

  // Auto-save tracking
  const projectIdRef = useRef<string | null>(null);

  // Update project ID ref when available
  useEffect(() => {
    if (projectUuid) {
      projectIdRef.current = projectUuid;
      artworkForm.setValue("designProjectUuid", projectUuid);
    }
  }, [projectUuid, artworkForm]);

  // Auto-save logic
  const canSave = useCallback(() => {
    // Don't save during initialization or if not initialized
    if (!isInitialized || isInitializing || isProcessingWorkflow) {
      return false;
    }

    // Don't save if we don't have a project ID or form data
    if (!projectIdRef.current || !debouncedFormValues) {
      return false;
    }

    const formData = debouncedFormValues as Partial<DesignLabFormType>;
    if (!formData.compositions?.currentCompositionId) {
      return false;
    }

    const { artwork } = formData;
    if (artwork) {
      // Only validate placements that will actually be sent to the API
      // For front: only the selected placement (front or frontLeftChest)
      // For back: always validate
      const isLeftChest = artwork.selectedFrontPlacement === "frontLeftChest";
      const activeFrontPlacement = isLeftChest ? artwork.frontLeftChest : artwork.front;
      const placementsToValidate = [activeFrontPlacement, artwork.back].filter(Boolean);

      const hasInvalidPlacement = placementsToValidate.some(
        placement =>
          placement?.artworkId &&
          placement?.size &&
          (placement.size.width <= 0 || placement.size.height <= 0)
      );

      if (hasInvalidPlacement) {
        return false;
      }
    }

    return true;
  }, [isInitialized, isInitializing, isProcessingWorkflow, debouncedFormValues]);

  // Auto-save effect
  useEffect(() => {
    if (!canSave()) {
      return;
    }

    const formData = debouncedFormValues as DesignLabFormType;
    const payload = formToApiPayload(formData);
    const payloadString = JSON.stringify(payload);

    // Don't save if nothing changed
    if (payloadString === lastSavedDataRef.current) {
      return;
    }

    lastSavedDataRef.current = payloadString;

    if (projectIdRef.current) {
      updateProject.mutate({
        id: projectIdRef.current,
        payload
      });
    }
  }, [canSave, debouncedFormValues, updateProject, lastSavedDataRef]);

  // Memoize return object to prevent unnecessary re-renders of all consumers
  // Only update when actual values change, not on every provider render
  return useMemo(
    () => ({
      // Initialization state
      initStage: stage,
      isInitialized,
      isInitializing,
      initError,

      // Project state
      projectUuid,
      projectData,
      placements: projectData?.placements,

      // Products
      productsMap,

      // Workflow state
      activeWorkflow,
      isProcessingWorkflow,

      // Modal states
      isDesignLibraryOpen,
      isCanvaOpen,
      isAiDesignOpen,

      // Save state
      isSaving: updateProject.isPending,
      lastSavedAt: updateProject.data ? new Date() : undefined,
      saveError: updateProject.error,

      // Overall loading state
      isLoading: isInitializing || updateProject.isPending,
      isRestoring: isInitializing && !isInitialized,

      // Actions
      retryInit,
      reset,

      // Legacy compatibility
      isEditMode: false,
      assemblyGroupId: undefined,
      savedProject, // Use the raw GraphQL response for backward compatibility
      error: initError || updateProject.error
    }),
    [
      stage,
      isInitialized,
      isInitializing,
      initError,
      projectUuid,
      projectData,
      productsMap,
      activeWorkflow,
      isProcessingWorkflow,
      isDesignLibraryOpen,
      isCanvaOpen,
      isAiDesignOpen,
      updateProject.isPending,
      updateProject.data,
      updateProject.error,
      retryInit,
      reset,
      savedProject
    ]
  );
}

export const [DesignProjectSyncProvider, useDesignProjectSync] = constate(
  useDesignProjectSyncCreator
);
